import { useCallback, useEffect, useState } from "react";
import { useSetAtom } from "jotai";
import LoadingState from "../components/basic/loading-state";
import { activePopupState } from "../state/components/popup";
import { resolveMediaUrl } from "../helpers/media-url";
import Queries from "../params/queries";
import type {
  FetchApi,
  FetchMultipart,
  GirlModelSummary,
} from "../params/types";

function placeLine(m: {
  city?: string | null;
  country?: string | null;
  id?: string;
}) {
  const place = [m.city, m.country].filter(Boolean).join(", ");
  return place || m.id || "";
}

function initialLetter(label?: string | null) {
  const s = String(label || "?").trim();
  return (s[0] || "?").toUpperCase();
}

interface ModelsProps {
  fetchApi: FetchApi;
  fetchMultipart?: FetchMultipart | null;
  busy?: boolean;
  setBusy?: (v: boolean) => void;
  setError?: (msg: string) => void;
  flashSuccess?: (msg: string) => void;
}

export default function Models({
  fetchApi,
  fetchMultipart,
  busy,
  setBusy,
  setError,
  flashSuccess,
}: ModelsProps) {
  const [summaries, setSummaries] = useState<GirlModelSummary[]>([]);
  const [loadingList, setLoadingList] = useState(true);
  const [localBusy, setLocalBusy] = useState(false);
  const [loadError, setLoadError] = useState("");
  const setSettingsPopup = useSetAtom(activePopupState("account_settings"));

  const isBusy = Boolean(busy || localBusy);

  const loadList = useCallback(async () => {
    try {
      const res = await fetchApi(Queries.Models);
      if (res?.errors) {
        setLoadError(res.errors[0]?.message || "Failed to load models");
        return [];
      }
      const rows = res?.models || [];
      setSummaries(rows);
      setLoadError("");
      return rows;
    } finally {
      setLoadingList(false);
    }
  }, [fetchApi]);

  useEffect(() => {
    let cancelled = false;
    // eslint-disable-next-line react-hooks/set-state-in-effect -- initial list load
    loadList();
    return () => {
      cancelled = true;
      void cancelled;
    };
  }, [loadList]);

  const openModelSettings = async (id?: string | null) => {
    if (!id) {
      setSettingsPopup({
        active: true,
        mode: "model",
        isNewModel: true,
        girlModel: null,
        fetchApi,
        fetchMultipart,
        flashSuccess,
        setError,
        busy,
        onModelsChanged: loadList,
      });
      return;
    }

    setLocalBusy(true);
    setBusy?.(true);
    try {
      const res = await fetchApi(Queries.Model, { id });
      if (res?.errors) {
        setError?.(res.errors[0]?.message || "Failed to load model");
        return;
      }
      const model = res?.model;
      if (!model) {
        setError?.("Model not found");
        return;
      }
      setSettingsPopup({
        active: true,
        mode: "model",
        isNewModel: false,
        girlModel: model,
        fetchApi,
        fetchMultipart,
        flashSuccess,
        setError,
        busy,
        onModelsChanged: loadList,
      });
    } finally {
      setLocalBusy(false);
      setBusy?.(false);
    }
  };

  return (
    <div className="ib-models">
      <header className="ib-models__toolbar">
        <div>
          <h2 className="ib-models__title">Models</h2>
          <p className="ib-models__subtitle">
            Girl personalities stored in git — persona, prompts, and gallery.
          </p>
        </div>
        <button
          type="button"
          className="jf-btn jf-btn-primary"
          disabled={isBusy}
          onClick={() => openModelSettings(null)}
        >
          New model
        </button>
      </header>

      {loadError ? (
        <p className="ib-models__error">{loadError}</p>
      ) : loadingList && summaries.length === 0 ? (
        <LoadingState label="Loading models…" />
      ) : summaries.length === 0 ? (
        <div className="ib-models-empty-state">
          <strong>No models yet</strong>
          <span>Create a pack, then apply it from account settings.</span>
          <button
            type="button"
            className="jf-btn jf-btn-primary"
            disabled={isBusy}
            onClick={() => openModelSettings(null)}
          >
            Create first model
          </button>
        </div>
      ) : (
        <div className="ib-models-grid">
          {summaries.map((m) => (
            <button
              key={m.id}
              type="button"
              className="ib-model-card"
              disabled={isBusy}
              onClick={() => openModelSettings(m.id)}
            >
              <div className="ib-model-card__cover">
                {m.cover_url ? (
                  <img src={resolveMediaUrl(m.cover_url)} alt="" />
                ) : (
                  <span className="ib-model-card__initial">
                    {initialLetter(m.label || m.first_name)}
                  </span>
                )}
              </div>
              <div className="ib-model-card__body">
                <div className="ib-model-card__name">
                  {m.label || m.first_name || m.id}
                </div>
                <div className="ib-model-card__meta">{placeLine(m)}</div>
                <div className="ib-model-card__stats">
                  <span>{m.gallery_count} media</span>
                  <span className="ib-model-card__id">{m.id}</span>
                </div>
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
